'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS, InviteData, LinkData, User } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import { getVerifyPermission, constructQuery } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getGroup, getGroupRoles, getGroupUsers } from 'src/api/admin/groups';
import UsersTab from 'src/parts/tabs/UsersTab';

export const Route = createFileRoute('/_admin/groups/$groupId/users')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			page: (search.page as number) || undefined,
			sort: (search.sort as string) || undefined,
			filter: (search.filter as string) || undefined,
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const [group, roles, users] = await Promise.all([
			getGroup(params.groupId),
			getGroupRoles(params.groupId),
			getGroupUsers(params.groupId, { page: deps.page, sort: deps.sort, filter: deps.filter }),
		]);
		return { group, roles, users };
	},
	component: UsersPage,
});

function UsersPage() {
	const { groupId } = Route.useParams();
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { group: initialGroup, roles: initialRoles, users: initialUsers } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();
	const { config } = useConfig();
	const defaultCountry = config?.defaultCountry || 'BE';

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const { data: group, refetch: refetchGroup } = useQuery({
		queryKey: ['group', groupId],
		queryFn: () => getGroup(groupId),
		initialData: initialGroup,
	});

	const { data: roles } = useQuery({
		queryKey: ['group-roles', groupId],
		queryFn: () => getGroupRoles(groupId),
		initialData: initialRoles,
	});

	const query = constructQuery({
		page: searchParams.page,
		sort: searchParams.sort,
		filter: searchParams.filter,
	});

	const { data: users, refetch: refetchUsers } = useQuery({
		queryKey: ['group-users', groupId, query],
		queryFn: () =>
			getGroupUsers(groupId, { page: searchParams.page, sort: searchParams.sort, filter: searchParams.filter }),
		initialData: initialUsers,
	});

	const revalidateGroupDetail = () => {
		refetchGroup();
		queryClient.invalidateQueries({ queryKey: ['group', groupId] });
		queryClient.invalidateQueries({ queryKey: ['group-users', groupId] });
	};

	const revalidateUsers = () => {
		refetchUsers();
		queryClient.invalidateQueries({ queryKey: ['group-users', groupId] });
	};

	const handleLinkUser = async (linkData: LinkData) => {
		const { userId, roleIds } = linkData;

		if (!userId) {
			return;
		}

		try {
			await request.post(`/api/groups/${groupId}/users`, {
				userId,
				roleIds,
			});
			EventEmitter.emit('toast', { type: 'success', message: 'Saved' });
		} catch (error: any) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: error.message || 'Error adding user to company',
			});
		}
		revalidateGroupDetail();
	};

	const handleInviteUser = async (data: InviteData) => {
		try {
			const { body: user } = await request.post<User>('/api/invites/users', data);
			await handleLinkUser({ userId: user.id, roleIds: data.roleIds });
			return user;
		} catch (error: any) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: error.message || 'Error adding user to company',
			});
		}
	};

	const handleDeleteUser = async (userId: string) => {
		try {
			await request.del(`/api/groups/${groupId}/users/${userId}`, {});
			EventEmitter.emit('toast', { type: 'success', message: 'Saved' });
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Error removing user from company',
			});
		}
		revalidateGroupDetail();
	};

	const handleReload = () => {
		revalidateUsers();
	};

	if (!group) {
		return null;
	}

	return (
		<UsersTab
			onInvite={handleInviteUser}
			onLink={handleLinkUser}
			onDelete={handleDeleteUser}
			onReload={handleReload}
			group={group}
			roles={roles || []}
			authenticatedUser={authenticatedUser!}
			users={users}
			readOnly={false}
			filter={searchParams.filter}
			permissions={{
				archive: verifyPermission(PERMISSIONS.adminUsersArchive),
				update: verifyPermission(PERMISSIONS.adminUsersEdit),
				create: verifyPermission(PERMISSIONS.adminUsersCreate),
				canPrintBadges: false,
				canCreateWithoutEmail: verifyPermission(PERMISSIONS.adminUsersCreate),
				isUserAdmin: verifyPermission(PERMISSIONS.adminUsersCreate),
				canImport: verifyPermission(PERMISSIONS.adminUsersImport),
			}}
			backUrlLocalStorageKey="usersBackUrl"
			defaultCountry={defaultCountry}
		/>
	);
}
